import { queryOptions } from "@tanstack/react-query";

import { getOperatorsHttpOrigin } from "../../operators";
import {
  readOperatorsAuthErrorMessage,
  throwOperatorsAuthExpired,
} from "../../operatorsAuthSession";
import { OperatorsForbiddenError, operatorsQueryKeys } from "../../lib/operatorsReactQuery";
import {
  type WebsiteTrafficSnapshotResponse,
  isWebsiteTrafficSnapshotResponse,
} from "./websiteTrafficTypes";

/** Domain-local query keys/fetchers for Website Traffic. */
export const websiteTrafficQueryKeys = {
  all: [...operatorsQueryKeys.all, "dashboards", "website-traffic"] as const,
  snapshot: () => [...websiteTrafficQueryKeys.all, "snapshot"] as const,
};

function throwIfUnauthorized(response: Response): void {
  if (response.status === 401) {
    throwOperatorsAuthExpired();
  }
}

async function throwIfForbidden(response: Response, fallbackMessage: string): Promise<void> {
  if (response.status !== 403) {
    return;
  }

  const message = await readOperatorsAuthErrorMessage(response).catch(() => fallbackMessage);
  throw new OperatorsForbiddenError(message || fallbackMessage);
}

export async function fetchWebsiteTrafficSnapshot(): Promise<WebsiteTrafficSnapshotResponse> {
  const response = await fetch(`${getOperatorsHttpOrigin()}/api/v1/dashboards/website-traffic`, {
    credentials: "include",
  });
  throwIfUnauthorized(response);
  await throwIfForbidden(response, "You do not have access to Dashboards.");
  if (!response.ok) {
    throw new Error(
      await readOperatorsAuthErrorMessage(response).catch(
        () => "Failed to load Website Traffic dashboard",
      ),
    );
  }
  const body: unknown = await response.json();
  if (!isWebsiteTrafficSnapshotResponse(body)) {
    throw new Error("Website Traffic response was malformed");
  }
  return body;
}

export function websiteTrafficSnapshotQueryOptions() {
  return queryOptions({
    queryKey: websiteTrafficQueryKeys.snapshot(),
    queryFn: fetchWebsiteTrafficSnapshot,
  });
}
